import { useQuery, useQueryClient } from '@tanstack/react-query'
import { chatBuilderService } from '@/services/chatBuilder'
import { useAuthStore } from '@/stores/auth'
import type { ChatConfiguration } from '@/stores/chatBuilder/types'
import { configurationKeys } from './useConfigurations'

/**
 * Bounded-poll control for `useConfiguration`. A number (ms) polls; `false`
 * stops. The function form receives the query's latest configuration and is
 * re-evaluated by TanStack after each fetch — this is how the onboarding
 * brand-avatar live-swap (see useAvatarLiveSwap) decides, from the config's own
 * provenance, whether to keep polling until the async-generated avatar lands.
 */
/**
 * The config fields the poll-control function reads. Narrower than the full
 * `ChatConfiguration` on purpose: TanStack always hands it the full config (a
 * superset, so assignable), and keeping the contract to exactly what's read —
 * provenance plus the avatar url — lets callers build honest test doubles
 * without casting a whole config or asset.
 */
export interface RefetchIntervalConfig {
  avatarSource?: ChatConfiguration['avatarSource']
  aiMessengerIconAsset?: Pick<
    NonNullable<ChatConfiguration['aiMessengerIconAsset']>,
    'url'
  >
}

export type ConfigurationRefetchInterval =
  | number
  | false
  | ((configuration: RefetchIntervalConfig | undefined) => number | false)

interface UseConfigurationOptions {
  enabled?: boolean
  staleTime?: number
  refetchOnMount?: boolean | 'always'
  /** Default `false` — no polling for normal builder use. */
  refetchInterval?: ConfigurationRefetchInterval
}

/**
 * Hook for fetching a single chat configuration with React Query caching
 * Automatically enabled when ID is provided
 */
export function useConfiguration(
  id: string | undefined | null,
  options: UseConfigurationOptions = {}
) {
  // Use selector to get only organizationId to prevent unnecessary re-renders
  const organizationId = useAuthStore((state) => state.organization?.id)
  const {
    enabled = true,
    staleTime = 5 * 60 * 1000,
    refetchOnMount = 'always',
    refetchInterval = false,
  } = options // 5 minutes default stale time
  const queryKey = configurationKeys.detail(id || '', organizationId)

  const {
    data: configurationResponse,
    isLoading,
    error,
    refetch,
    isFetching,
    isRefetching,
  } = useQuery({
    queryKey,
    queryFn: () => {
      if (!id) {
        throw new Error('Configuration ID is required')
      }

      return chatBuilderService.getConfiguration(id)
    },
    enabled: enabled && !!id && !!organizationId,
    staleTime, // How long data stays fresh
    gcTime: 10 * 60 * 1000, // 10 minutes - keep in cache
    refetchOnWindowFocus: false, // Don't refetch when returning to tab (editing in progress)
    refetchOnReconnect: true, // Refetch when internet reconnects
    refetchOnMount, // Configurable refetch behavior
    // Bounded polling (onboarding avatar live-swap); false = off. When a
    // function is given, adapt TanStack's query-form to our typed config-form.
    refetchInterval:
      typeof refetchInterval === 'function'
        ? (query) => refetchInterval(query.state.data?.data)
        : refetchInterval,
  })

  const configuration = configurationResponse?.data

  return {
    // Data
    configuration,

    // Org switch info
    requiresOrgSwitch: configurationResponse?.requiresOrgSwitch,
    targetOrganization: configurationResponse?.targetOrganization,

    // Convenience booleans
    isPublished: configuration?.status === 'published',
    isDraft: configuration?.status === 'draft',
    isInactive: configuration?.status === 'inactive',

    // Loading states
    isLoading, // First time loading
    isFetching, // Loading or background refetching
    isRefetching, // Background refetching only

    // Error
    error,

    // Actions
    refetch,
  }
}

/**
 * Hook for prefetching a configuration
 * Useful when you know a configuration will be needed soon
 */
export function usePrefetchConfiguration() {
  const { organization } = useAuthStore()
  const queryClient = useQueryClient()

  return async (id: string) => {
    if (!id || !organization?.id) return

    // This will populate the React Query cache without triggering a component re-render
    return await queryClient.prefetchQuery({
      queryKey: configurationKeys.detail(id, organization?.id),
      queryFn: () => chatBuilderService.getConfiguration(id),
      staleTime: 5 * 60 * 1000, // 5 minutes - same as useConfiguration
    })
  }
}
